import React from 'react';
import { WrapperContainer, PageHeader } from '../../constants/style/common.styled';
import Heading from '../../components/typography';
import Search from '../../components/search/search';
import { useAllInOneImportExport } from '../../hooks/custom-hooks/useAllInOneImportExport';
import { useTemplateSearch } from '../../hooks/custom-hooks/useTemplateSearch';
import ImportExportBlock from '../../components/import-export/ImportExportBlock';

/**
 * All-in-One Import/Export Page
 *
 * Provides centralized access to all import/export functionality across the application.
 * Features include:
 * - Searchable list of all import/export modules
 * - Standardized UI for download and import operations
 * - Alphabetically sorted for easy navigation
 *
 * @returns JSX.Element - The rendered All-in-One Import page
 */
const AllInOneImport: React.FC = () => {
  // Get all import/export functionality from custom hook
  const { templateDatas } = useAllInOneImportExport();

  // Search functionality
  const {
    searchValue,
    filteredItems: filteredTemplateDatas,
    handleSearch,
    hasResults,
  } = useTemplateSearch({
    items: templateDatas,
    searchFields: ['title'],
  });

  // Check if no modules are available (due to permissions or empty data)
  const hasNoModules = templateDatas.length === 0;
  const hasNoSearchResults = !hasResults && searchValue.trim() !== '';

  return (
    <div id='allInOneImportContainer'>
      <PageHeader>
        <Heading variant='h3' title='All In One Import/Export' />
        <Heading variant='body-s' title='Centralized import/export actions across modules.' />
      </PageHeader>

      {!hasNoModules && (
        <div style={{ marginBottom: '24px' }}>
          <Search
            id='allInOneImportSearch'
            placeholder='Search import/export modules...'
            searchValue={searchValue}
            onSearch={handleSearch}
          />
        </div>
      )}

      {hasNoModules ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Heading variant='body' title='No import/export modules available.' />
          <Heading
            variant='body-s'
            title='You may not have the required permissions to access import/export functionality.'
            color='#7A7A7A'
            style={{ marginTop: '8px' }}
          />
        </div>
      ) : hasResults ? (
        <ImportExportBlock
          id='allInOneImportBlock'
          title='Quick & easy to import/export large volumes of data in an instant.'
          templateDatas={filteredTemplateDatas}
        />
      ) : hasNoSearchResults ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Heading variant='body' title={`No import/export modules found for "${searchValue}"`} />
        </div>
      ) : null}
    </div>
  );
};

export default AllInOneImport;
